// src/components/admin/ContasCupons/AllCouponsTab.tsx
// Controle de Todos os Cupons - IDENTIDADE GRAIA

import React, { useState } from 'react';
import {
    Ticket,
    Search,
    Eye,
    EyeOff,
    Trash2,
    X,
    AlertCircle,
    Filter,
    Link as LinkIcon,
    Shield,
} from 'lucide-react';
import './AllCouponsTab.css';
import { useAllCoupons, useInvalidate } from '../../../hooks/useAdminQueries';

interface Coupon {
    _id: string;
    code: string;
    discountPercent: number;
    createdByVendorId: {
        _id: string;
        username: string;
        role: string;
    } | null;
    productId: {
        _id: string;
        title: string;
    } | null;
    isLink: boolean;
    active: boolean;
    timesUsed: number;
    usedByUsers: string[];
    createdAt: string;
}

interface AllCouponsTabProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const AllCouponsTab: React.FC<AllCouponsTabProps> = ({ onShowToast }) => {

    // ── 1. useState PRIMEIRO ──────────────────────────────
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
    const [filterType, setFilterType] = useState<'all' | 'generic' | 'link'>('all');

    // ── 2. React Query hooks DEPOIS ───────────────────────
    const { data, isLoading: loading } = useAllCoupons();
    const invalidate = useInvalidate();

    // ── 3. Derivados ──────────────────────────────────────
    const coupons: Coupon[] = data?.coupons ?? [];

    const handleToggleCoupon = async (couponId: string, currentStatus: boolean) => {
        const action = currentStatus ? 'desativar' : 'ativar';
        if (!window.confirm(`Tem certeza que deseja ${action} este cupom?`)) return;

        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/sales/coupons/${couponId}/toggle`,
                {
                    method: 'PATCH',
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.ok) {
                onShowToast(currentStatus ? 'Cupom desativado' : 'Cupom ativado', 'success');
                invalidate.allCoupons();
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao alterar cupom', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao alterar cupom', 'error');
        }
    };

    const handleDeleteCoupon = async (couponId: string, couponCode: string) => {
        if (!window.confirm(`Tem certeza que deseja DELETAR o cupom "${couponCode}"?\n\n⚠️ Esta ação é irreversível!`)) return;

        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/sales/coupons/${couponId}`,
                {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.ok) {
                onShowToast('Cupom deletado com sucesso', 'success');
                invalidate.allCoupons();
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao deletar cupom', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao deletar cupom', 'error');
        }
    };

    const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('pt-BR');

    const filteredCoupons = coupons.filter((coupon: Coupon) => {
        const matchesSearch = coupon.code.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus =
            filterStatus === 'all' ||
            (filterStatus === 'active' && coupon.active) ||
            (filterStatus === 'inactive' && !coupon.active);
        const matchesType =
            filterType === 'all' ||
            (filterType === 'generic' && !coupon.isLink) ||
            (filterType === 'link' && coupon.isLink);
        return matchesSearch && matchesStatus && matchesType;
    });

    if (loading) {
        return (
            <div className="all-cpn-loading">
                <div className="spinner"></div>
                <p>Carregando cupons...</p>
            </div>
        );
    }

    return (
        <div className="all-cpn-container">
            <div className="all-cpn-header">
                <div>
                    <h3>Controle de Cupons</h3>
                    <p className="all-cpn-subtitle">
                        Gerencie todos os cupons criados na plataforma ({coupons.length} total)
                    </p>
                </div>
            </div>

            {/* Filtros */}
            <div className="all-cpn-filters">
                <div className="all-cpn-search-bar">
                    <Search size={20} />
                    <input
                        type="text"
                        placeholder="Buscar por código..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="all-cpn-btn-clear" onClick={() => setSearchTerm('')}>
                            <X size={18} />
                        </button>
                    )}
                </div>

                <div className="all-cpn-filter-group">
                    <Filter size={16} />
                    <span>Status:</span>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}>
                        <option value="all">Todos</option>
                        <option value="active">Ativos</option>
                        <option value="inactive">Inativos</option>
                    </select>
                </div>

                <div className="all-cpn-filter-group">
                    <span>Tipo:</span>
                    <select value={filterType} onChange={(e) => setFilterType(e.target.value as typeof filterType)}>
                        <option value="all">Todos</option>
                        <option value="generic">Genéricos</option>
                        <option value="link">Links</option>
                    </select>
                </div>
            </div>

            {/* Alert de vendedores órfãos */}
            {coupons.some((c: Coupon) => !c.createdByVendorId) && (
                <div className="all-cpn-orphan-alert">
                    <AlertCircle size={20} />
                    <span>Existem cupons de vendedores que foram removidos da plataforma. Você pode deletá-los aqui.</span>
                </div>
            )}

            {/* Lista de Cupons */}
            {filteredCoupons.length === 0 ? (
                <div className="all-cpn-empty">
                    <Shield size={64} strokeWidth={1.5} />
                    <h3>Nenhum cupom encontrado</h3>
                    <p>Ajuste os filtros ou crie novos cupons</p>
                </div>
            ) : (
                <div className="all-cpn-table-container">
                    <table className="all-cpn-table">
                        <thead>
                            <tr>
                                <th>Código</th>
                                <th>Tipo</th>
                                <th>Desconto</th>
                                <th>Criador</th>
                                <th>Produto</th>
                                <th>Usos</th>
                                <th>Criado em</th>
                                <th>Status</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCoupons.map((coupon: Coupon) => (
                                <tr key={coupon._id} className={!coupon.active ? 'inactive' : ''}>
                                    <td><span className="all-cpn-code-badge">{coupon.code}</span></td>
                                    <td>
                                        {coupon.isLink ? (
                                            <span className="all-cpn-type-badge link"><LinkIcon size={14} /> Link</span>
                                        ) : (
                                            <span className="all-cpn-type-badge generic"><Ticket size={14} /> Genérico</span>
                                        )}
                                    </td>
                                    <td><span className="all-cpn-discount">{coupon.discountPercent}%</span></td>
                                    <td>
                                        {coupon.createdByVendorId ? (
                                            <div className="all-cpn-vendor-cell">
                                                <span className="all-cpn-vendor-name">{coupon.createdByVendorId.username}</span>
                                                {coupon.createdByVendorId.role === 'admin' && (
                                                    <span className="all-cpn-admin-badge"><Shield size={12} /> Admin</span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="all-cpn-orphan"><AlertCircle size={14} /> Removido</span>
                                        )}
                                    </td>
                                    <td>
                                        {coupon.productId ? (
                                            <span className="all-cpn-product">{coupon.productId.title}</span>
                                        ) : (
                                            <span className="all-cpn-no-product">-</span>
                                        )}
                                    </td>
                                    <td><span className="all-cpn-uses">{coupon.timesUsed || 0}x</span></td>
                                    <td className="all-cpn-date">{formatDate(coupon.createdAt)}</td>
                                    <td>
                                        <span className={`all-cpn-status ${coupon.active ? 'active' : 'inactive'}`}>
                                            {coupon.active ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="all-cpn-actions">
                                            <button
                                                className={`all-cpn-btn-toggle ${coupon.active ? 'active' : ''}`}
                                                onClick={() => handleToggleCoupon(coupon._id, coupon.active)}
                                                title={coupon.active ? 'Desativar' : 'Ativar'}
                                            >
                                                {coupon.active ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                            <button
                                                className="all-cpn-btn-delete"
                                                onClick={() => handleDeleteCoupon(coupon._id, coupon.code)}
                                                title="Deletar"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AllCouponsTab;